import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  RowSelectionState,
  OnChangeFn,
  ColumnSizingState,
  ColumnSizingInfoState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual'

import { rankItem } from '@tanstack/match-sorter-utils';

// CSS styles for vertical lines - SIMPLE approach
const verticalLinesStyles = `
  .table-with-vertical-lines th,
  .table-with-vertical-lines td {
    border-left: 1px solid currentColor;
    border-right: 1px solid currentColor;
    border-left-color: rgba(0,0,0,0.125);
    border-right-color: rgba(0,0,0,0.125);
  }
  
  .table-with-vertical-lines thead th {
    border-top: 1px solid rgba(0,0,0,0.125);
  }
  
  [data-bs-theme="dark"] .table-with-vertical-lines th,
  [data-bs-theme="dark"] .table-with-vertical-lines td {
    border-left-color: rgba(255,255,255,0.125);
    border-right-color: rgba(255,255,255,0.125);
  }
  
  [data-bs-theme="dark"] .table-with-vertical-lines thead th {
    border-top-color: rgba(255,255,255,0.125);
  }
`;

const columnResizerStyles = `
/* default subtle */
.column-resizer {
  opacity: 0.3;
  transition: opacity 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

/* show clearly on hover/focus (keyboard) */
.column-resizer:hover,
.column-resizer:focus {
  opacity: 1;
}

/* optional: when resizing, force visible */
th[aria-resizing='true'] .column-resizer,
.column-resizer.is-resizing {
  opacity: 1;
  background-color: rgba(0,0,0,0.08);
} `

// Column Filter
const Filter = ({
  column
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input {...props} value={value} id="search-bar-0" className="form-control border-0 search" onChange={e => setValue(e.target.value)} />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  isGlobalFilter?: any;
  handleTaskClick?: any;
  customPageSize?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  thClass?: any;
  divClass?: any;
  SearchPlaceholder?: any;
  handleLeadClick?: any;
  handleCompanyClick?: any;
  handleContactClick?: any;
  handleTicketClick?: any;
  isBordered?: any;
  hidePagination?: boolean;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (row: any) => string;
  enableMultiRowSelection?: boolean;
  onRowClick?: (row: any) => void;
  verticalLines?: boolean;
  enableVirtualScroll?: boolean;
  tableBodyHeight?: number | string;
  allowModeToggle?: boolean;
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder,
  isBordered,
  hidePagination,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange,
  getRowId,
  enableMultiRowSelection = true,
  onRowClick,
  verticalLines = false,
  enableVirtualScroll = false,
  tableBodyHeight = '60vh',
  allowModeToggle = false

}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');


  // Column resizing state (typed)
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>({} as ColumnSizingInfoState);

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      ...(enableRowSelection && { rowSelection }),
      columnSizing,
      columnSizingInfo,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    columnResizeMode: 'onChange', // Enable column resizing
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(enableRowSelection && {
      enableRowSelection: true,
      enableMultiRowSelection,
      onRowSelectionChange,
      getRowId,
    }),
  });

  const [localIsVirtual, setLocalIsVirtual] = useState<boolean>(enableVirtualScroll);
  useEffect(() => {
    setLocalIsVirtual(enableVirtualScroll);
  }, [enableVirtualScroll]);

  const rows = localIsVirtual
    ? table.getPrePaginationRowModel().rows
    : table.getRowModel().rows;

  const parentRef = React.useRef<HTMLDivElement>(null);

  // Virtualizer for virtual scrolling
  const virtualizer = useVirtualizer({
    count: localIsVirtual ? rows.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 34,
    overscan: 20,
  });

  const virtualItems = localIsVirtual ? virtualizer.getVirtualItems() : [];
  const totalSize = localIsVirtual ? virtualizer.getTotalSize() : 0;
  const paddingTop = localIsVirtual && virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = localIsVirtual && virtualItems.length > 0 ? (totalSize - virtualItems[virtualItems.length - 1].end) : 0;
  const visibleColumnCount = table.getVisibleFlatColumns().length;


  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
    getPageCount
  } = table;


  //helper function to get pagination data
  const generatePaginationNumbers = () => {
    const totalPages = getPageCount();
    const current = getState().pagination.pageIndex;
    const delta = 2; // Show 2 pages before/after current

    // Handle edge cases
    if (totalPages <= 1) return [];
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages = [];

    // Always show first page
    pages.push(0);

    // Calculate the range around current page
    const rangeStart = Math.max(1, current - delta);
    const rangeEnd = Math.min(totalPages - 2, current + delta);

    // Add ellipsis if there's a gap between first page and current range
    if (rangeStart > 1) {
      pages.push('...');
    }

    // Add current page range (skip if it would overlap with first or last page)
    for (let i = rangeStart; i <= rangeEnd; i++) {
      if (i !== 0 && i !== totalPages - 1) {
        pages.push(i);
      }
    }

    // Add ellipsis if there's a gap between current range and last page
    if (rangeEnd < totalPages - 2) {
      pages.push('...');
    }

    // Always show last page (if different from first)
    if (totalPages > 1) {
      pages.push(totalPages - 1);
    }

    return pages;
  };

  useEffect(() => {
    Number(customPageSize) && setPageSize(Number(customPageSize));
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      {isGlobalFilter && <Row className="mb-3">
        <CardBody className="border border-dashed border-end-0 border-start-0">
          <form>
            <Row>
              <Col sm={5}>
                <div className="search-box me-2 mb-2 d-inline-block col-12">
                  <DebouncedInput
                    value={globalFilter ?? ''}
                    onChange={value => setGlobalFilter(String(value))}
                    placeholder={SearchPlaceholder}
                  />
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </Col>
            </Row>
          </form>
        </CardBody>
      </Row>}

      {verticalLines && <style>{verticalLinesStyles}</style>}

      <div className="d-flex align-items-center justify-content-end mb-2">
        {allowModeToggle && (
          <div className="btn-group btn-group-sm" role="group" aria-label="Table display mode">
            <button
              type="button"
              className={`btn ${!localIsVirtual ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setLocalIsVirtual(false)}
              aria-pressed={!localIsVirtual}
            >
              Pagination
            </button>
            <button
              type="button"
              className={`btn ${localIsVirtual ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setLocalIsVirtual(true)}
              aria-pressed={localIsVirtual}
            >
              Scrolling
            </button>
          </div>
        )}
      </div>

      <div ref={localIsVirtual ? parentRef : undefined} className={divClass} style={localIsVirtual ? { height: tableBodyHeight, overflowY: 'auto' } : undefined}>
        <div style={localIsVirtual ? { minHeight: `${virtualizer.getTotalSize()}px` } : undefined}>
          <Table hover style={{ width: '100%' }} className={`${tableClass} ${verticalLines ? 'table-with-vertical-lines' : ''}`}>
            <thead className={theadClass} style={{ height: '20px' }}>
              {getHeaderGroups().map((headerGroup: any) => (
                <tr className={trClass} key={headerGroup.id}>
                  {headerGroup.headers.map((header: any) => (
                    <th style={{ position: 'relative' }} key={header.id} className={`py-1 px-2 ${thClass}`}  {...{
                      'aria-sort': ((): 'none' | 'ascending' | 'descending' => {
                        const sorted = header.column.getIsSorted() as 'asc' | 'desc' | false;
                        if (sorted === 'asc') return 'ascending';
                        if (sorted === 'desc') return 'descending';
                        return 'none';
                      })(),
                    }}>
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          <span className="d-inline-flex align-items-center">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanSort?.() && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 ms-1 align-middle"
                                aria-label={`Toggle sort for ${header.column.id}`}
                                aria-pressed={!!header.column.getIsSorted()}
                                title="Toggle sort"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const handler = header.column.getToggleSortingHandler();
                                  // @ts-ignore
                                  handler(e);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const handler = header.column.getToggleSortingHandler();
                                    // @ts-ignore
                                    handler(e);
                                  }
                                }}
                              >
                                {header.column.getIsSorted() === 'asc' && (
                                  <i className="ri-arrow-up-s-line text-primary small" aria-hidden="true"></i>
                                )}
                                {header.column.getIsSorted() === 'desc' && (
                                  <i className="ri-arrow-down-s-line text-primary small" aria-hidden="true"></i>
                                )}
                                {!header.column.getIsSorted() && (
                                  <i className="ri-arrow-up-down-line text-muted small" aria-hidden="true"></i>
                                )}
                              </button>
                            )}
                            {header.column.getCanResize() && (
                              <span
                                role="separator"
                                aria-orientation="vertical"
                                aria-label={`Resize column ${header.column.id}`}
                                tabIndex={0}
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className="column-resizer"
                                
                                style={{
                                  position: "absolute",
                                  // opacity: 0,
                                  top: 0,
                                  right: 0,
                                  height: "100%",
                                  width: "5px",
                                  // backgroundColor: "#27bbff",
                                  cursor: "col-resize",
                                  userSelect: "none",
                                  touchAction: "none",
                                  borderRadius: "6px 0 0 6px",

                                }}
                              />
                            )}
                          </span>
                          <style>{columnResizerStyles}</style>
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {localIsVirtual ? (
                <>
                  {paddingTop > 0 && (
                    <tr style={{ height: paddingTop }}>
                      {Array.from({ length: visibleColumnCount }).map((_, i) => (
                        <td key={i} />
                      ))}
                    </tr>
                  )}
                  {virtualItems.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    return (
                      <tr
                        key={row.id}
                        onClick={() => onRowClick?.(row)}
                        className={`${enableRowSelection && row.getIsSelected() ? 'table-active' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                        style={{ height: `${virtualRow.size}px` }}
                      >
                        {row.getVisibleCells().map((cell: any) => (
                          <td style={{ width: cell.column.getSize(), maxWidth: cell.column.getSize() }}>
                            <span style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }} className="d-block" title={String(cell.getValue() ?? '')}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </span>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {paddingBottom > 0 && (
                    <tr style={{ height: paddingBottom }}>
                      {Array.from({ length: visibleColumnCount }).map((_, i) => (
                        <td key={i} />
                      ))}
                    </tr>
                  )}
                </>
              ) : (
                getRowModel().rows.map((row: any) => (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick?.(row)}
                    className={`${enableRowSelection && row.getIsSelected() ? 'table-active' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                  >
                    {row.getVisibleCells().map((cell: any) => (
                      <td key={cell.id} className="py-1 px-2">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {!hidePagination && !localIsVirtual && (
        <Row className="align-items-center mt-2 text-center text-sm-start">
          <div className="col-sm">
            <div className="text-muted">Showing<span className="fw-semibold ms-1">{getState().pagination.pageSize}</span> of <span className="fw-semibold">{data.length}</span> Results
            </div>
          </div>
          <div className="col-sm-auto d-flex justify-content-center">
            <div className="pagination-wrapper" style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <ul className="pagination pagination-separated pagination-sm justify-content-center justify-content-sm-start mb-0" style={{ minWidth: 'max-content' }}>
                <li className={!getCanPreviousPage() ? "page-item disabled" : "page-item"}>
                  <Link to="#" className="page-link" onClick={previousPage}>Previous</Link>
                </li>
                {generatePaginationNumbers().map((item: any, key: number) => (
                  <React.Fragment key={key}>
                    {item === '...' ? (
                      <li className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    ) : (
                      <li className="page-item">
                        <Link to="#" className={getState().pagination.pageIndex === item ? "page-link active" : "page-link"} onClick={() => setPageIndex(item)}>{item + 1}</Link>
                      </li>
                    )}
                  </React.Fragment>
                ))}
                <li className={!getCanNextPage() ? "page-item disabled" : "page-item"}>
                  <Link to="#" className="page-link" onClick={nextPage}>Next</Link>
                </li>

                {/* Go to Page Input */}
                <li className="page-item">
                  <div className="d-flex align-items-center ms-2">
                    <span className="text-muted me-2 small">Go to:</span>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: '50px', height: '20px', fontSize: '0.55rem' }}
                      min="1"
                      max={getPageCount()}
                      placeholder="Page"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const target = e.target as HTMLInputElement;
                          const page = parseInt(target.value) - 1;
                          if (page >= 0 && page < getPageCount()) {
                            setPageIndex(page);
                            target.value = ''; // Clear input after navigation
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const target = e.target as HTMLInputElement;
                        const page = parseInt(target.value) - 1;
                        if (target.value && page >= 0 && page < getPageCount()) {
                          setPageIndex(page);
                          target.value = ''; // Clear input after navigation
                        }
                      }}
                    />
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;